import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, DownloadRow, EmptyState, Input, RadioCards, Select, Skeleton, StatusChip } from '@/ds'
import { dayMonth, dayMonthLong, formatMoney } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { pagosService } from '@/services/pagos'
import type { ClientType, FiscalData, InvoiceInfo } from '@/services/pagos'
import type { Stay } from '@/services/reserva'
import { useSave } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink, usePagosStay } from './shared'
import { adultContactEmail } from '@/services/reserva/holder'

const EMPTY_FISCAL: FiscalData = { clientType: 'individual', name: '', surname: '', identifierTypeId: '', identifier: '', address: '', postalCode: '', city: '', countryId: '', provinceId: '', email: '' }

type FiscalErrors = Partial<Record<keyof FiscalData, string>>

/** Obligatorios de RequestInvoice.tsx para pedir la factura (E15). Guardar antes de la salida no exige nada. */
function validateFiscal(f: FiscalData, hasProvinces: boolean, t: (k: string) => string): FiscalErrors {
  const e: FiscalErrors = {}
  const req = t('v2.legacy.validations.Required')
  const inv = 'v2.legacy.preview.labels.requestInvoice.errors.required'
  if (!f.name.trim()) e.name = f.clientType === 'business' ? t(`${inv}.businessName`) : t('v2.legacy.stepper.collectDataForm.errors.name.required')
  if (f.clientType === 'individual') {
    if (!f.surname.trim()) e.surname = t('v2.legacy.stepper.collectDataForm.errors.surname1.required')
    if (!f.identifierTypeId) e.identifierTypeId = req
    if (!f.identifier.trim()) e.identifier = t('v2.legacy.stepper.collectDataForm.errors.identifier.required')
  }
  if (!f.address.trim()) e.address = t(`${inv}.street`)
  if (!f.postalCode.trim()) e.postalCode = t(`${inv}.zipCode`)
  if (!f.city.trim()) e.city = t(`${inv}.city`)
  if (!f.countryId) e.countryId = t('v2.legacy.stepper.collectDataForm.errors.countryId')
  if (hasProvinces && !f.provinceId) e.provinceId = t(`${inv}.province`)
  return e
}

/**
 * Datos fiscales (D35). El diseño pide "Nombre o empresa", "NIF o CIF" y "Dirección fiscal"; E15 exige los
 * campos de la app actual, así que cada bloque del diseño agrupa los suyos con sus etiquetas.
 */
function FiscalForm({ stay, info, onRequested }: { stay: Stay; info: InvoiceInfo; onRequested: (bills: { id: string; url: string }[]) => void }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [form, setForm] = useState<FiscalData>(info.saved ?? { ...EMPTY_FISCAL, identifierTypeId: stay.registration.catalogs.identifierTypes[0]?.id ?? '' })
  const [showErrors, setShowErrors] = useState(false)
  const [saved, setSaved] = useState(false)
  const { saving, error, run } = useSave()
  const [provinces] = useAsync(`provincias:${stay.guestCode}:${form.countryId}`, (signal) => (form.countryId ? pagosService.getProvinces(stay, form.countryId, signal) : Promise.resolve([])))
  const provinceList = provinces.kind === 'ready' ? provinces.value : []
  const errors = showErrors ? validateFiscal(form, provinceList.length > 0, t) : {}
  const set = (patch: Partial<FiscalData>) => {
    setForm((f) => ({ ...f, ...patch }))
    setSaved(false)
  }
  const business = form.clientType === 'business'

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const data = { ...form, provinceId: provinceList.length > 0 ? form.provinceId : '' }
    if (info.canRequest) {
      setShowErrors(true)
      if (Object.keys(validateFiscal(data, provinceList.length > 0, t)).length > 0) return
      const bills = await run(() => pagosService.requestInvoice(stay, data))
      if (bills) onRequested(bills)
    } else {
      const ok = await run(async () => {
        await pagosService.saveFiscalData(stay, data)
        return true
      })
      if (ok) setSaved(true)
    }
  }

  return (
    <form className="pagos__stack" onSubmit={(e) => void submit(e)} noValidate>
      <p>{info.fiscalDeadline ? t('v2.pagos.recibos.facturaIntro', { date: dayMonthLong(info.fiscalDeadline, lang) }) : t('v2.pagos.recibos.facturaIntroSinFecha')}</p>
      <RadioCards
        label={t('v2.legacy.preview.labels.requestInvoice.clientType.title')}
        columns={2}
        value={form.clientType}
        onChange={(id) => set({ clientType: id as ClientType })}
        options={[
          { id: 'individual', label: t('v2.legacy.preview.labels.requestInvoice.clientType.individual') },
          { id: 'business', label: t('v2.legacy.preview.labels.requestInvoice.clientType.business') },
        ]}
      />
      <div className="pagos__group">
        <Input label={t('v2.pagos.recibos.nombreEmpresa')} autoComplete={business ? 'organization' : 'given-name'} value={form.name} onChange={(e) => set({ name: e.target.value })} error={errors.name} />
        {!business && <Input label={t('v2.pagos.recibos.apellidos')} autoComplete="family-name" value={form.surname} onChange={(e) => set({ surname: e.target.value })} error={errors.surname} />}
      </div>
      <div className="pagos__group">
        {!business && (
          <Select
            label={t('v2.legacy.stepper.collectDataForm.fields.identifierTypeId')}
            options={stay.registration.catalogs.identifierTypes.map((o) => ({ id: o.id, label: o.name }))}
            value={form.identifierTypeId}
            onChange={(identifierTypeId) => set({ identifierTypeId })}
            error={errors.identifierTypeId}
          />
        )}
        <Input
          label={t('v2.pagos.recibos.nif')}
          labelNote={t('v2.pagos.recibos.nifNota')}
          placeholder={t('v2.pagos.recibos.nifPlaceholder')}
          help={info.canRequest ? undefined : t('v2.pagos.recibos.nifAyuda')}
          value={form.identifier}
          onChange={(e) => set({ identifier: e.target.value.toUpperCase() })}
          error={errors.identifier}
        />
      </div>
      <fieldset className="pagos__fieldset">
        <legend className="pagos__legend">{t('v2.pagos.recibos.direccion')}</legend>
        <Input label={t('v2.legacy.preview.labels.requestInvoice.labels.street')} autoComplete="street-address" value={form.address} onChange={(e) => set({ address: e.target.value })} error={errors.address} />
        <Input label={t('v2.legacy.preview.labels.requestInvoice.labels.zipCode')} autoComplete="postal-code" inputMode="numeric" value={form.postalCode} onChange={(e) => set({ postalCode: e.target.value })} error={errors.postalCode} />
        <Input label={t('v2.legacy.preview.labels.requestInvoice.labels.city')} autoComplete="address-level2" value={form.city} onChange={(e) => set({ city: e.target.value })} error={errors.city} />
        <Select
          label={t('v2.pagos.recibos.pais')}
          placeholder=" "
          options={stay.registration.catalogs.countries.map((o) => ({ id: o.id, label: o.name }))}
          value={form.countryId}
          onChange={(countryId) => set({ countryId, provinceId: '' })}
          error={errors.countryId}
          searchable
          searchLabel={t('v2.ds.select.buscarPais')}
          searchPlaceholder={t('v2.ds.select.buscarPais')}
          noResultsText={t('v2.ds.select.sinResultados')}
        />
        {provinceList.length > 0 && (
          <Select label={t('v2.legacy.preview.labels.requestInvoice.labels.province')} placeholder=" " options={provinceList.map((o) => ({ id: o.id, label: o.name }))} value={form.provinceId} onChange={(provinceId) => set({ provinceId })} error={errors.provinceId} />
        )}
      </fieldset>
      <Input label={t('v2.legacy.preview.labels.requestInvoice.labels.email')} labelNote={t('v2.pagos.recibos.opcional')} type="email" autoComplete="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
      <div aria-live="polite" className="pagos__group">
        {error && (
          <p className="pagos__error" role="alert">
            {error}
          </p>
        )}
        {saved && <StatusChip tone="success">{t('v2.pagos.recibos.guardado')}</StatusChip>}
      </div>
      <Button type="submit" loading={saving}>
        {info.canRequest ? t('v2.legacy.preview.labels.requestInvoice.requestInvoiceButton') : t('v2.pagos.recibos.guardar')}
      </Button>
    </form>
  )
}

/**
 * 8.3 · Recibos y factura (Pagos.dc.html screen=receipts, Bloque 3 §3h). Facturas (E1 `billList`) y petición
 * de factura (E15, provincias E7) reales; recibo por pago y reenvío por email contra mock; los datos fiscales
 * se guardan en el dispositivo antes de la salida (D22, D35).
 */
export function Recibos() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const stay = usePagosStay()
  const [receipts, reloadReceipts] = useAsync(`recibos:${stay.guestCode}`, (signal) => pagosService.getReceipts(stay, signal))
  const [info, reloadInfo] = useAsync(`factura:${stay.guestCode}`, (signal) => pagosService.getInvoiceInfo(stay, signal))
  const [requested, setRequested] = useState<{ id: string; url: string }[]>([])
  const [sentTo, setSentTo] = useState<string | null>(null)
  const resend = useSave()
  // Titular o primer adulto registrado; nunca el email de un menor (D23).
  const email = (info.kind === 'ready' ? info.value.saved?.email : null) || adultContactEmail(stay)
  const bills = [...(info.kind === 'ready' ? info.value.bills : []), ...requested.filter((r) => info.kind !== 'ready' || !info.value.bills.some((b) => b.id === r.id))]
  const list = receipts.kind === 'ready' ? receipts.value : []

  return (
    <main className="page pagos">
      <div className="pagos__head">
        <BackLink />
        <h1>{t('v2.pagos.recibos.titulo')}</h1>
        <p className="page__lead">{t(receipts.kind === 'ready' ? 'v2.pagos.recibos.lead' : 'v2.pagos.recibos.leadSinRecibos')}</p>
      </div>

      {receipts.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={200} />
        </div>
      )}
      {receipts.kind === 'error' && <SectionError code={receipts.code} onRetry={reloadReceipts} />}
      {receipts.kind === 'ready' && list.length === 0 && (
        <EmptyState icon="file-text" title={t('v2.pagos.recibos.sinRecibos')} headingLevel={2}>
          {t('v2.pagos.recibos.sinRecibosTexto')}
        </EmptyState>
      )}
      {list.length > 0 && (
        <Card flush as="section" aria-label={t('v2.pagos.recibos.recibos')}>
          {list.map((r) => (
            <DownloadRow key={r.id} title={r.concept} meta={t('v2.pagos.recibos.reciboMeta', { date: dayMonth(r.date, lang), amount: formatMoney(r.amount, lang) })} href={r.url} />
          ))}
        </Card>
      )}

      {stay.flags.showGetBills && (
        <>
          <h2 className="pagos__h2">{t('v2.pagos.recibos.factura')}</h2>
          {bills.length > 0 && (
            <Card flush>
              {bills.map((b) => (
                <DownloadRow key={b.id} title={t('v2.pagos.recibos.facturaNumero', { id: b.id })} href={b.url} />
              ))}
            </Card>
          )}
          {info.kind === 'loading' && (
            <div aria-busy="true">
              <Skeleton height={320} />
            </div>
          )}
          {info.kind === 'error' && <SectionError code={info.code} onRetry={reloadInfo} />}
          {info.kind === 'ready' && bills.length === 0 && <FiscalForm stay={stay} info={info.value} onRequested={setRequested} />}
        </>
      )}

      {receipts.kind === 'ready' && list.length > 0 && email && (
        <div aria-live="polite" className="pagos__group">
          {sentTo ? (
            <StatusChip tone="success">{t('v2.pagos.recibos.reenviados', { email: sentTo })}</StatusChip>
          ) : (
            <Button
              variant="secondary"
              icon="mail"
              loading={resend.saving}
              onClick={() =>
                void resend.run(() => pagosService.resendReceipts(stay, email)).then((r) => {
                  if (r) setSentTo(r.sentTo)
                })
              }
            >
              {t('v2.pagos.recibos.reenviar', { email })}
            </Button>
          )}
          {resend.error && (
            <p className="pagos__error" role="alert">
              {resend.error}
            </p>
          )}
        </div>
      )}
    </main>
  )
}
